import React, { useRef, useState } from 'react';

import { Foot, fmt, Grid, Note, Panel, Pill, plural, Segmented, Table } from './parts';
import {
    assess, CRITERIA_FIELDS, criteriaError, DEFAULT_CRITERIA, evidenceFromCsv, evidenceFromDemo,
    MAX_IMPORT_BYTES, outcomeLabel, reportRows, templateCsv
} from './assessment';
import { dateTime } from './model';
import { download, downloadCsv } from './exports';

const OUTCOME_TONE = { within: 'ok', advisory: 'warn', none: 'muted' };

function Results({ record, sim }) {
    const exportCsv = () => {
        downloadCsv(`SmartDC_Demo_${record.id}_Report.csv`, reportRows(record));
        sim.notify('Report exported as a CSV.');
    };

    const exportJson = () => {
        download(
            `SmartDC_Demo_${record.id}_Evidence.json`,
            JSON.stringify({ note: 'Demonstration record. Not a compliance certificate.', ...record }, null, 2),
            'application/json'
        );
        sim.notify('Evidence exported as JSON.');
    };

    return (
        <Panel
            title="Assessment results"
            sub={`${record.id} · ${record.evidence.meterId}`}
            badge={<Pill tone={record.evidence.origin === 'MEASURED' ? 'muted' : 'info'}>{record.evidence.origin === 'MEASURED' ? 'Imported as measured' : 'Simulated snapshot'}</Pill>}
        >
            <div className="dash-verdict">
                <div>
                    <h3 className={`dash-verdict__title dash-verdict__title--${record.tone}`}>{record.verdict}</h3>
                    <p>
                        {record.counts.within} within criteria · {plural(record.counts.advisory, 'advisory', 'advisories')} · {record.counts.none} not assessed
                    </p>
                    <p className="dash-verdict__times">
                        Evidence from {dateTime(record.evidence.timestamp)} · Run {dateTime(record.ranAt)}
                    </p>
                </div>

                <div className="dash-panel__side">
                    <button type="button" className="dash-btn" onClick={exportCsv}>CSV report</button>
                    <button type="button" className="dash-btn" onClick={exportJson}>JSON evidence</button>
                </div>
            </div>

            <Table className="dash-table--wrap">
                <thead>
                    <tr>
                        <th scope="col">Check</th>
                        <th scope="col">Measured result</th>
                        <th scope="col">Operating criterion</th>
                        <th scope="col">Outcome</th>
                    </tr>
                </thead>
                <tbody>
                    {record.checks.map(check => (
                        <tr key={check.name}>
                            <th scope="row">
                                {check.name}
                                <span className="dash-table__note">{check.note}</span>
                            </th>
                            <td>{check.measured}</td>
                            <td>{check.criterion}</td>
                            <td><Pill tone={OUTCOME_TONE[check.outcome]}>{outcomeLabel(check.outcome)}</Pill></td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Foot>A snapshot compared with criteria you configured. It is not a site compliance certificate or an executed equipment test. Every report freezes its source and its limits, so changing the selectors afterwards does not change a saved result.</Foot>
        </Panel>
    );
}

const COMMISSIONING = [
    ['Instrument & methods', 'Meter serial, firmware, IEC method class and standard edition.'],
    ['Measurement chain', 'CT/VT ratios, polarity, neutral channel and phase mapping.'],
    ['Time & calibration', 'Clock source and error, calibration and transducer uncertainty.'],
    ['Approved test plan', 'Load steps, battery and bypass transfers with synchronized input and output recordings.']
];

export function Tests({ sim }) {
    const { ac, now, tests, setTests } = sim;
    const { criteria, records, currentId, tab, imported } = tests;
    const fileInput = useRef(null);
    const [importError, setImportError] = useState(null);

    const update = patch => setTests(state => ({ ...state, ...patch }));
    const setCriterion = (key, value) => update({ criteria: { ...criteria, [key]: value } });

    const error = criteriaError(criteria);
    const needsFile = tab === 'import' && !imported;
    const current = records.find(record => record.id === currentId) || null;

    const run = () => {
        const evidence = tab === 'import' ? imported : evidenceFromDemo(ac, now);
        const id = `PQ-${String(tests.counter + 1).padStart(4, '0')}`;
        const record = assess(evidence, criteria, id, Date.now());

        update({ counter: tests.counter + 1, records: [record, ...records], currentId: id });
        sim.notify(record.counts.advisory
            ? 'Assessment saved. Review the highlighted advisories.'
            : 'Assessment saved. Review the checks that remain unassessed.');
    };

    const onFile = async event => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;

        setImportError(null);

        if (file.size > MAX_IMPORT_BYTES) {
            setImportError(`That file is over the ${MAX_IMPORT_BYTES / 1024} kB limit.`);
            update({ imported: null });
            return;
        }

        try {
            update({ imported: evidenceFromCsv(await file.text()) });
        } catch (problem) {
            setImportError(problem.message);
            update({ imported: null });
        }
    };

    const downloadTemplate = () => {
        download('SmartDC_Demo_Snapshot_Template.csv', templateCsv(evidenceFromDemo(ac, now)));
        sim.notify('Template downloaded, with simulated example values.');
    };

    return (
        <>
            <div className="dash-strip">
                <div>
                    <strong>Capture. Compare. Document.</strong>
                    <p>Run a spot assessment from the demo meter, or import a three-phase measurement snapshot. Results use your operating criteria and keep a reviewable record.</p>
                </div>
                <Pill>No equipment control</Pill>
            </div>

            <Grid>
                <Panel title="1. Select the evidence" sub="One physical meter location per assessment">
                    <Segmented
                        label="Evidence source"
                        value={tab}
                        onChange={value => update({ tab: value })}
                        options={[{ value: 'demo', label: 'Demo meter' }, { value: 'import', label: 'Import measurement CSV' }]}
                    />

                    {tab === 'demo' ? (
                        <div className="dash-evidence">
                            <span className="dash-eyebrow">Selected measurement point</span>
                            <strong>{ac.meter.label} <Pill tone="info">Path {ac.path}</Pill></strong>
                            <p>{ac.meterTag} · {ac.profile.ll} V L–L · {ac.profile.hz} Hz</p>
                            <dl>
                                <div><dt>Active power</dt><dd>{fmt(ac.totalKw)} kW</dd></div>
                                <div><dt>Worst current THD</dt><dd>{ac.maxThdI === null ? '—' : `${fmt(ac.maxThdI)} %`}</dd></div>
                                <div><dt>Data origin</dt><dd>Simulated</dd></div>
                            </dl>
                            <p className="dash-hint">The toolbar chooses the path and meter. To see an advisory fire, choose Path A and PDU / rack feed.</p>
                        </div>
                    ) : (
                        <div className="dash-evidence">
                            <div className="dash-drop">
                                <strong>Choose a meter snapshot CSV</strong>
                                <p>Three-phase, four-wire wye: L1, L2 and L3 at one timestamp, up to {MAX_IMPORT_BYTES / 1024} kB.</p>
                                <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFile} aria-label="Meter snapshot CSV file" />
                            </div>

                            {importError && <p className="dash-error" role="alert">{importError}</p>}

                            {imported && (
                                <p className="dash-ok" role="status">
                                    Loaded {imported.meterId}, marked {imported.origin}. Schema, phase coverage and power consistency passed.
                                </p>
                            )}

                            <button type="button" className="dash-link" onClick={downloadTemplate}>Download the template, with simulated example values</button>
                            <Note>Files are read in this browser and are not uploaded anywhere. Use data_mode MEASURED for actual meter values and keep SIMULATED for examples. Source calibration and measurement methods remain unverified, and the report keeps the distinction.</Note>
                        </div>
                    )}

                    <div className="dash-run">
                        <div>
                            <strong>Spot PQ assessment</strong>
                            <p>Eight operating checks plus two assessment-readiness checks.</p>
                        </div>
                        <button type="button" className="dash-btn dash-btn--primary" onClick={run} disabled={Boolean(error) || needsFile}>Run assessment</button>
                    </div>

                    {needsFile && !error && <p className="dash-hint">Choose a CSV file above to run the assessment on it.</p>}
                </Panel>

                <Panel
                    title="2. Set operating criteria"
                    sub="Advisory limits, editable for your project"
                    actions={<button type="button" className="dash-link" onClick={() => update({ criteria: DEFAULT_CRITERIA })}>Reset</button>}
                >
                    <div className="dash-fields">
                        {CRITERIA_FIELDS.map(field => (
                            <label className="dash-field" key={field.key}>
                                <span>{field.label}</span>
                                <span className="dash-field__input">
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step={field.step}
                                        min={field.min}
                                        max={field.max}
                                        value={criteria[field.key]}
                                        onChange={event => setCriterion(field.key, event.target.value)}
                                    />
                                    <small>{field.unit}</small>
                                </span>
                            </label>
                        ))}
                    </div>

                    {error && <p className="dash-error" role="alert">{error}</p>}

                    <details className="dash-details">
                        <summary>Demand reference & PCC assessment inputs</summary>
                        <div className="dash-fields">
                            <label className="dash-field">
                                <span>Maximum demand current I<sub>L</sub></span>
                                <span className="dash-field__input">
                                    <input type="number" min="0" placeholder="Not configured" value={criteria.demandCurrent} onChange={event => setCriterion('demandCurrent', event.target.value)} />
                                    <small>A</small>
                                </span>
                            </label>
                            <label className="dash-field">
                                <span>Short-circuit current I<sub>SC</sub></span>
                                <span className="dash-field__input">
                                    <input type="number" min="0" placeholder="Not configured" value={criteria.shortCircuit} onChange={event => setCriterion('shortCircuit', event.target.value)} />
                                    <small>A</small>
                                </span>
                            </label>
                        </div>
                    </details>

                    <Foot>Criteria apply only to this assessment and do not change protective-device settings. Flicker, events, transient withstand and long-term compliance need their own completed observation windows and acceptance specifications.</Foot>
                </Panel>
            </Grid>

            {current ? (
                <Results record={current} sim={sim} />
            ) : (
                <Note>No assessment run yet. Run one to see each measured value, criterion and result, and export them as a CSV report or a JSON record.</Note>
            )}

            <Grid>
                <Panel
                    title="Assessment history"
                    sub="This page session only · reloading clears it"
                    actions={records.length > 0 && (
                        <button
                            type="button"
                            className="dash-link"
                            onClick={() => {
                                update({ records: [], currentId: null });
                                sim.notify('Local assessment history cleared.');
                            }}
                        >
                            Clear history
                        </button>
                    )}
                >
                    {records.length === 0 ? (
                        <Note>No assessments yet. A new run freezes the selected evidence and criteria.</Note>
                    ) : (
                        <ul className="dash-history">
                            {records.map(record => (
                                <li key={record.id}>
                                    <button type="button" aria-pressed={record.id === currentId} onClick={() => update({ currentId: record.id })}>
                                        <span>
                                            <strong>{record.id} · {record.evidence.meterId}</strong>
                                            <small>{dateTime(record.ranAt)} · {record.evidence.origin}</small>
                                        </span>
                                        <Pill tone={record.tone}>{record.short}</Pill>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="Commissioning evidence" sub="Required before connected or contractual acceptance" badge={<Pill>Not supplied</Pill>}>
                    <dl className="dash-defs">
                        {COMMISSIONING.map(([term, definition]) => (
                            <div key={term}>
                                <dt>{term}</dt>
                                <dd>{definition}</dd>
                            </div>
                        ))}
                    </dl>
                    <Foot>This workspace evaluates records. It does not create voltage disturbances, operate a load bank, switch a UPS or execute an electrical commissioning procedure.</Foot>
                </Panel>
            </Grid>
        </>
    );
}
